import {
    User,
    UserManager,
    UserManagerSettings,
    WebStorageStateStore
} from 'oidc-client-ts';
import { proxy } from 'valtio';
import { getRequiredEnvVars } from './utils';

interface OidcUserStore {
    user: User | null;
}

export const oidcUserStore = proxy<OidcUserStore>({
    user: null,
});

const { AUTHORITY_URL, CLIENT_ID, HOST } = getRequiredEnvVars([
    'AUTHORITY_URL',
    'CLIENT_ID',
    'HOST',
]);

const scopes = [
    'openid',
    'profile',
    'email',
    'phone',
    'tenant',
    'theme',
    'offline_access',
    'fv.api.gateway.access',
    'fv.auth.tenant.read',
    'filevine.v2.webhooks',
    'filevine.v2.api.*'
];

const settings: UserManagerSettings = {
    authority: AUTHORITY_URL,
    client_id: CLIENT_ID,
    redirect_uri: `${window.location.origin}/signin`,
    silent_redirect_uri: `${HOST}/silent-callback.html`,
    post_logout_redirect_uri: `${HOST}/logout`,
    response_type: 'code',
    response_mode: 'query',
    automaticSilentRenew: true,
    monitorSession: true,
    scope: scopes.join(' '),
    userStore: new WebStorageStateStore({ store: window.localStorage })
}

export const userManager = new UserManager(settings);

userManager.events.addUserLoaded((user) => {
    oidcUserStore.user = user;
});

userManager.events.addUserSignedOut(() => {
    userManager.removeUser();
    window.location.href = '/';
});

export const signIn = async () => {
    await userManager.signinRedirect({
        prompt: 'select_tenant',
    });
}

export const signinCallback = async () => {
    await userManager.signinCallback();
}

export const loadUser = async () => {
    const user = await userManager.getUser();
    oidcUserStore.user = user;
    return user;
}

const renewToken = async (): Promise<string | undefined> => {
    try {
        const user = await userManager.signinSilent();
        oidcUserStore.user = user;
        return user?.access_token;
    } catch (error) {
        console.error('Error renewing token:', error);
        return undefined;
    }
};

export const getAccessToken = async (): Promise<string | undefined> => {
    const { user } = oidcUserStore;

    // If user is expired, renew the token
    if (user?.expired) {
        return renewToken();
    }

    // If user has a valid access token, return it
    if (user?.access_token) {
        return user.access_token;
    }

    // If user exists but doesn't have an access token, try to renew
    if (user) {
        return renewToken();
    }

    // No user, throw error
    throw new Error('User is not logged in.');
};

export const signout = () => userManager.signoutSilent();